import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import './App.css';

// Admin-only: remove a player's score from a game's current-year leaderboard.
const DeletePlayerControl = () => {
   const authHeader = useAuthHeader();
   const [gameOptions, setGameOptions] = useState([]);
   const [selectedGame, setSelectedGame] = useState('');
   const [players, setPlayers] = useState([]);
   const [loadingPlayers, setLoadingPlayers] = useState(false);
   const [confirmingName, setConfirmingName] = useState(null);
   const [status, setStatus] = useState(null);

   useEffect(() => {
      const loadGames = async () => {
         try {
            const response = await axios.get('/games/current');
            setGameOptions(response.data || []);
         } catch (err) {
            console.error('Failed to load games:', err);
            setStatus({ text: 'Failed to load games. Please refresh the page.', type: 'error' });
         }
      };
      loadGames();
   }, []);

   const loadPlayers = async (gameShortName) => {
      if (!gameShortName) {
         setPlayers([]);
         return;
      }
      setLoadingPlayers(true);
      try {
         const response = await fetch(`/topScores?game=${gameShortName}`);
         const data = await response.json();
         setPlayers(data.players || []);
      } catch (err) {
         console.error('Failed to load players:', err);
         setStatus({ text: 'Failed to load players for that game.', type: 'error' });
      } finally {
         setLoadingPlayers(false);
      }
   };

   const handleGameChange = (e) => {
      const shortName = e.target.value;
      setSelectedGame(shortName);
      setConfirmingName(null);
      setStatus(null);
      loadPlayers(shortName);
   };

   const handleDelete = async (playerName) => {
      try {
         await axios.delete('/deletePlayer', {
            params: { game: selectedGame, name: playerName },
            headers: { Authorization: authHeader() },
         });
         setStatus({ text: `Removed ${playerName} from the leaderboard.`, type: 'success' });
         setConfirmingName(null);
         loadPlayers(selectedGame);
      } catch (err) {
         console.error(err);
         const msg = err.response?.data?.error ?? 'Could not reach the server.';
         setStatus({ text: msg, type: 'error' });
      }
   };

   return (
      <div className="main-style">
         <div className="player-entry-form">
            <p>
               Remove a single player's score from a game's current leaderboard.
               This only removes that score entry, not the player's other scores.
            </p>

            {status && (
               <div className={status.type === 'success' ? 'success-message' : 'error-message'}>
                  {status.text}
               </div>
            )}

            <div className="margin-top-medium">
               <label className="form-label" htmlFor="deleteGameSelect">Select a Game:</label>
               <select
                  className="form-select full-width"
                  id="deleteGameSelect"
                  value={selectedGame}
                  onChange={handleGameChange}
               >
                  <option value="">--Select a Game--</option>
                  {gameOptions.map((game) => (
                     <option key={game.shortName} value={game.shortName}>{game.displayName}</option>
                  ))}
               </select>
            </div>

            {loadingPlayers && <p className="margin-top-medium">Loading players...</p>}

            {!loadingPlayers && selectedGame && players.length === 0 && (
               <p className="margin-top-medium">No scores recorded yet for this game.</p>
            )}

            {!loadingPlayers && players.length > 0 && (
               <table className="scoreboard-table margin-top-medium">
                  <thead>
                     <tr>
                        <th>Player Name</th>
                        <th>Score</th>
                        <th></th>
                     </tr>
                  </thead>
                  <tbody>
                     {players.map((player) => (
                        <tr key={player.playerName}>
                           <td>{player.playerName}</td>
                           <td>{player.score}</td>
                           <td>
                              {confirmingName === player.playerName ? (
                                 <>
                                    <button
                                       type="button"
                                       className="btn-standard"
                                       onClick={() => handleDelete(player.playerName)}
                                    >
                                       Confirm
                                    </button>
                                    <button
                                       type="button"
                                       className="btn-standard"
                                       onClick={() => setConfirmingName(null)}
                                    >
                                       Cancel
                                    </button>
                                 </>
                              ) : (
                                 <button
                                    type="button"
                                    className="btn-standard"
                                    onClick={() => setConfirmingName(player.playerName)}
                                 >
                                    Delete
                                 </button>
                              )}
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            )}
         </div>
      </div>
   );
};

export default DeletePlayerControl;
